import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { userService, workshopService } from "../services/dataService";
import { authService } from "../services/authService";
import { User, Workshop } from "../types";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./UserManagement.css";

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    workshopIds: [] as string[], // Changed to array
    isActive: true,
    password: "",
  });

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state for delete
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string;
  }>({ isOpen: false, userId: null, userName: "" });

  const roles = [
    { value: "Manager", label: "Müdür" },
    { value: "Sekreterya", label: "Sekreterya" },
    { value: "Modelist", label: "Modelist" },
    { value: "Üretim", label: "Üretim" },
    { value: "Muhasebe", label: "Muhasebe" },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, workshopsData] = await Promise.all([
        userService.getAll(),
        workshopService.getAll(),
      ]);
      setUsers(usersData);
      setWorkshops(workshopsData);
    } catch (error) {
      console.error("Error loading data:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenirken hata oluştu",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      workshopIds: user.workshops?.map((w) => w.workshopId) || [],
      isActive: user.isActive,
      password: "",
    });
  };

  const handleCancel = () => {
    setEditingUser(null);
    setIsAddModalOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      workshopIds: [],
      isActive: true,
      password: "",
    });
  };

  const handleAddUser = () => {
    setIsAddModalOpen(true);
    setEditingUser(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      workshopIds: [],
      isActive: true,
      password: "",
    });
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.password || formData.password.trim().length < 6) {
      setAlertModal({
        isOpen: true,
        message: "Şifre en az 6 karakter olmalıdır",
        type: "warning",
      });
      return;
    }

    try {
      const createData = {
        username: formData.email, // Email'i username olarak kullan
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        workshopIds:
          formData.workshopIds.length > 0 ? formData.workshopIds : undefined,
        isActive: true,
        createdBy: currentUser?.userId || "unknown",
      };

      await authService.register(createData);
      setAlertModal({
        isOpen: true,
        message: `Kullanıcı "${formData.firstName} ${formData.lastName}" başarıyla oluşturuldu!`,
        type: "success",
      });
      handleCancel();
      loadData();
    } catch (error: any) {
      console.error("Error creating user:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı oluşturulamadı: " +
          (error.response?.data?.message || error.message),
        type: "error",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      // Sadece gerekli alanları gönder
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
      };

      // WorkshopIds varsa ekle
      if (formData.workshopIds.length > 0) {
        updateData.workshopIds = formData.workshopIds;
      }

      // Şifre girilmişse ekle (en az 6 karakter olmalı)
      if (formData.password && formData.password.trim().length >= 6) {
        updateData.password = formData.password.trim();
      }

      console.log("Update data being sent:", updateData);
      await userService.update(editingUser.userId, updateData);
      setAlertModal({
        isOpen: true,
        message: "Kullanıcı başarıyla güncellendi",
        type: "success",
      });
      handleCancel();
      loadData();
    } catch (error) {
      console.error("Error updating user:", error);
      setAlertModal({
        isOpen: true,
        message: "Kullanıcı güncellenirken hata oluştu",
        type: "error",
      });
    }
  };

  const handleDelete = (user: User) => {
    setConfirmModal({
      isOpen: true,
      userId: user.userId,
      userName: `${user.firstName} ${user.lastName}`,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmModal.userId) return;

    try {
      await userService.delete(confirmModal.userId);
      setAlertModal({
        isOpen: true,
        message: `Kullanıcı "${confirmModal.userName}" başarıyla silindi`,
        type: "success",
      });
      setConfirmModal({ isOpen: false, userId: null, userName: "" });
      loadData();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı silinirken hata oluştu: " +
          (error.response?.data?.message || error.message),
        type: "error",
      });
      setConfirmModal({ isOpen: false, userId: null, userName: "" });
    }
  };

  const getRoleLabel = (role: string) => {
    const roleObj = roles.find((r) => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const getWorkshopNames = (
    workshops: { workshopId: string; name: string }[] | undefined,
  ) => {
    if (!workshops || workshops.length === 0) return "-";
    return workshops.map((w) => w.name).join(", ");
  };

  const isManager = currentUser?.role === "Manager";

  if (loading) {
    return <div className="user-management-loading">Yükleniyor...</div>;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>Kullanıcı Yönetimi</h1>
        {!editingUser && !isAddModalOpen && (
          <button onClick={handleAddUser} className="btn-add-user">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
            Yeni Kullanıcı Ekle
          </button>
        )}
      </div>

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Yeni Kullanıcı Ekle</h2>
              <button className="modal-close" onClick={handleCancel}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Ad *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Soyad *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rol *</label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    required
                  >
                    <option value="">Seçiniz</option>
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Atölyeler{" "}
                    {(formData.role === "Üretim" ||
                      formData.role === "Modelist") &&
                      "*"}
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(150px, 1fr))",
                      gap: "6px",
                      padding: "8px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      maxHeight: "150px",
                      overflowY: "auto",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    {workshops.map((workshop) => (
                      <label
                        key={workshop.workshopId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          cursor: "pointer",
                          padding: "4px 6px",
                          fontSize: "13px",
                          borderRadius: "4px",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f0f0f0")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <input
                          type="checkbox"
                          value={workshop.workshopId}
                          checked={formData.workshopIds.includes(
                            workshop.workshopId,
                          )}
                          onChange={(e) => {
                            const workshopId = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              workshopIds: e.target.checked
                                ? [...prev.workshopIds, workshopId]
                                : prev.workshopIds.filter(
                                    (id) => id !== workshopId,
                                  ),
                            }));
                          }}
                          style={{ width: "14px", height: "14px" }}
                        />
                        <span>{workshop.name}</span>
                      </label>
                    ))}
                  </div>
                  {(formData.role === "Üretim" ||
                    formData.role === "Modelist") && (
                    <small>
                      Bu rol için en az bir atölye seçimi zorunludur
                    </small>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Şifre *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                  minLength={6}
                  placeholder="En az 6 karakter"
                />
                <small>Şifre en az 6 karakter olmalıdır</small>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-cancel"
                >
                  İptal
                </button>
                <button type="submit" className="btn-save">
                  Kullanıcı Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="user-management-content">
        {editingUser ? (
          <div className="user-edit-form">
            <h2>Kullanıcı Düzenle</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Ad *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Soyad *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rol *</label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    disabled={!isManager}
                    required
                  >
                    <option value="">Seçiniz</option>
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {!isManager && <small>Sadece müdür rol değiştirebilir</small>}
                </div>

                <div className="form-group">
                  <label>Atölyeler</label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(150px, 1fr))",
                      gap: "6px",
                      padding: "8px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      maxHeight: "150px",
                      overflowY: "auto",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    {workshops.map((workshop) => (
                      <label
                        key={workshop.workshopId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          cursor: "pointer",
                          padding: "4px 6px",
                          fontSize: "13px",
                          borderRadius: "4px",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f0f0f0")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <input
                          type="checkbox"
                          value={workshop.workshopId}
                          checked={formData.workshopIds.includes(
                            workshop.workshopId,
                          )}
                          onChange={(e) => {
                            const workshopId = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              workshopIds: e.target.checked
                                ? [...prev.workshopIds, workshopId]
                                : prev.workshopIds.filter(
                                    (id) => id !== workshopId,
                                  ),
                            }));
                          }}
                          style={{ width: "14px", height: "14px" }}
                        />
                        <span>{workshop.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {isManager && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                    Aktif
                  </label>
                  {!formData.isActive && (
                    <small style={{ color: "#dc3545" }}>
                      Kullanıcı pasif yapıldığında sisteme giriş yapamaz
                    </small>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Yeni Şifre (opsiyonel)</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Değiştirmek istemiyorsanız boş bırakın"
                />
                <small>
                  Şifre değiştirmek istemiyorsanız bu alanı boş bırakın
                </small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-cancel"
                >
                  İptal
                </button>
                <button type="submit" className="btn-save">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="users-list">
            <table>
              <thead>
                <tr>
                  <th>Ad Soyad</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Atölye</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.userId}
                    className={!user.isActive ? "inactive-user" : ""}
                  >
                    <td>
                      {user.firstName} {user.lastName}
                    </td>
                    <td>{user.email}</td>
                    <td>{getRoleLabel(user.role)}</td>
                    <td>
                      {user.workshops && user.workshops.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            maxWidth: "250px",
                          }}
                        >
                          {user.workshops.map((workshop) => (
                            <span
                              key={workshop.workshopId}
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                backgroundColor: "#e3f2fd",
                                color: "#1976d2",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "500",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {workshop.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: "#999", fontStyle: "italic" }}>
                          -
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          user.isActive ? "active" : "inactive"
                        }`}
                      >
                        {user.isActive ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => handleEdit(user)}
                          className="btn-edit"
                        >
                          Düzenle
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="btn-delete"
                          disabled={user.userId === currentUser?.userId}
                          title={
                            user.userId === currentUser?.userId
                              ? "Kendi hesabınızı silemezsiniz"
                              : "Kullanıcıyı sil"
                          }
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Kullanıcıyı Sil"
        message={`"${confirmModal.userName}" kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        type="danger"
        confirmText="Evet, Sil"
        cancelText="İptal"
        onConfirm={handleConfirmDelete}
        onCancel={() =>
          setConfirmModal({ isOpen: false, userId: null, userName: "" })
        }
      />
    </div>
  );
};

export default UserManagement;
